import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Plus, Pencil, Trash2, Newspaper, X, Save, Calendar, Image } from 'lucide-react';
import { toast } from 'sonner';
import FileUploader from '../../components/admin/FileUploader';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminNews = () => {
  const { getAuthHeader } = useAdminAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNews, setEditingNews] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const emptyNews = {
    title: { pl: '', en: '' },
    content: { pl: '', en: '' },
    excerpt: { pl: '', en: '' },
    imageUrl: '',
    isPublished: false,
    publishedAt: ''
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/news?all=true`, {
        headers: getAuthHeader()
      });
      setNews(response.data);
    } catch (error) {
      console.error('Failed to fetch news:', error);
      toast.error('Nie udało się pobrać danych');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await axios.post(`${API}/api/admin/news`, editingNews, {
          headers: getAuthHeader()
        });
        toast.success('Aktualność utworzona');
      } else {
        await axios.put(`${API}/api/admin/news/${editingNews.news_id}`, editingNews, {
          headers: getAuthHeader()
        });
        toast.success('Aktualność zaktualizowana');
      }
      setEditingNews(null);
      setIsCreating(false);
      fetchNews();
    } catch (error) {
      toast.error('Nie udało się zapisać');
    }
  };

  const handleDelete = async (newsId) => {
    if (!confirm('Czy na pewno chcesz usunąć tę aktualność?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/news/${newsId}`, {
        headers: getAuthHeader()
      });
      toast.success('Aktualność usunięta');
      fetchNews();
    } catch (error) {
      toast.error('Nie udało się usunąć');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Zarządzanie Aktualnościami</h1>
          <p className="text-gray-400">Aktualności i wpisy blogowe</p>
        </div>
        <Button
          onClick={() => { setEditingNews(emptyNews); setIsCreating(true); }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Dodaj Aktualność
        </Button>
      </div>

      {/* News List */}
      <div className="grid gap-4">
        {news.map((item) => (
          <Card key={item.news_id} className="bg-white/5 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 bg-white/10 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Newspaper className="w-8 h-8 text-gray-500" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-medium truncate">
                      {item.title?.pl || item.title?.en || 'Bez tytułu'}
                    </h3>
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      item.isPublished 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-gray-700 text-gray-400'
                    }`}>
                      {item.isPublished ? 'Opublikowany' : 'Szkic'}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm truncate mt-1">
                    {item.excerpt?.pl || item.excerpt?.en || 'Brak opisu'}
                  </p>
                  {item.publishedAt && (
                    <p className="text-gray-500 text-xs mt-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(item.publishedAt).toLocaleDateString('pl-PL', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { setEditingNews(item); setIsCreating(false); }}
                    className="text-gray-400 hover:text-white"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(item.news_id)}
                    className="text-gray-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {news.length === 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8 text-center">
              <Newspaper className="w-12 h-12 mx-auto text-gray-500 mb-4" />
              <p className="text-gray-400">Brak aktualności</p>
              <Button
                onClick={() => { setEditingNews(emptyNews); setIsCreating(true); }}
                variant="outline"
                className="mt-4"
              >
                Dodaj pierwszą aktualność
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Modal */}
      {editingNews && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10">
              <CardTitle className="text-white">
                {isCreating ? 'Nowa Aktualność' : 'Edytuj Aktualność'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingNews(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label className="text-gray-300">Obrazek</Label>
                <FileUploader
                  onUpload={(url) => setEditingNews({ ...editingNews, imageUrl: url })}
                  accept="image/*"
                  currentUrl={editingNews.imageUrl}
                />
                {/* Fallback: manual URL input */}
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-gray-500 text-xs">lub wklej URL:</span>
                  <Input
                    value={editingNews.imageUrl || ''}
                    onChange={(e) => setEditingNews({ ...editingNews, imageUrl: e.target.value })}
                    placeholder="https://..."
                    className="bg-white/5 border-white/10 text-white text-sm h-8 flex-1"
                  />
                </div>
              </div>

              {/* Title */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Tytuł (PL)</Label>
                  <Input
                    value={editingNews.title?.pl || ''}
                    onChange={(e) => setEditingNews({ 
                      ...editingNews, 
                      title: { ...editingNews.title, pl: e.target.value } 
                    })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Tytuł (EN)</Label>
                  <Input
                    value={editingNews.title?.en || ''}
                    onChange={(e) => setEditingNews({ 
                      ...editingNews, 
                      title: { ...editingNews.title, en: e.target.value } 
                    })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Zajawka (PL)</Label>
                  <Textarea
                    value={editingNews.excerpt?.pl || ''}
                    onChange={(e) => setEditingNews({ 
                      ...editingNews, 
                      excerpt: { ...editingNews.excerpt, pl: e.target.value } 
                    })}
                    rows={2}
                    className="bg-white/5 border-white/10 text-white resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Zajawka (EN)</Label>
                  <Textarea
                    value={editingNews.excerpt?.en || ''}
                    onChange={(e) => setEditingNews({ 
                      ...editingNews, 
                      excerpt: { ...editingNews.excerpt, en: e.target.value } 
                    })}
                    rows={2}
                    className="bg-white/5 border-white/10 text-white resize-none"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Treść (PL)</Label>
                  <Textarea
                    value={editingNews.content?.pl || ''}
                    onChange={(e) => setEditingNews({ 
                      ...editingNews, 
                      content: { ...editingNews.content, pl: e.target.value } 
                    })}
                    rows={6}
                    className="bg-white/5 border-white/10 text-white resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Treść (EN)</Label>
                  <Textarea
                    value={editingNews.content?.en || ''}
                    onChange={(e) => setEditingNews({ 
                      ...editingNews, 
                      content: { ...editingNews.content, en: e.target.value } 
                    })}
                    rows={6}
                    className="bg-white/5 border-white/10 text-white resize-none"
                  />
                </div>
              </div>

              {/* Publish Settings */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={editingNews.isPublished}
                    onCheckedChange={(checked) => setEditingNews({ 
                      ...editingNews, 
                      isPublished: checked,
                      publishedAt: checked && !editingNews.publishedAt ? new Date().toISOString() : editingNews.publishedAt
                    })}
                  />
                  <Label className="text-gray-300">Opublikowany</Label>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-300 text-sm">Data publikacji</Label>
                  <Input
                    type="datetime-local"
                    value={editingNews.publishedAt ? new Date(editingNews.publishedAt).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setEditingNews({ ...editingNews, publishedAt: e.target.value })}
                    className="bg-white/5 border-white/10 text-white w-52"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingNews(null); setIsCreating(false); }}>
                  Anuluj
                </Button>
                <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminNews;
